import {
  CLIENT_EVENTS, clientEventPolicy, clientSyncStall, prepareClientEvent, stackHash,
} from '../domain/clientEvents';
import { coreSync, loadCore } from './core.js';
import { APP_VERSION } from './appVersion';

// Telling the server what only this browser saw (#459). The allow-list, the
// field rules and the limiter's rule are `core/src/client_events.rs`, which the
// Android and iOS apps ask too (#496), through src/domain/clientEvents.js; this
// file decides *when*, keeps the page load's state, and sends.
//
// Nothing here may make things worse. Every path is fire-and-forget: a report
// never throws into its caller, never rejects, never retries, and never
// reports its own failure, which would loop. Repeats within a page load are
// dropped (the same error thrown from the same place is one event), and a page
// load sends at most the core's cap. The server caps per uid as well.
//
// The sender is installed, not imported: `src/main.jsx` hands this module the
// callable once Firebase is up. Until then — in every test, and in a build
// with no Firebase config — `reportClientEvent` does nothing, which keeps
// `firebase/functions` out of the import graph of everything that reports.
//
// ── Before the core ─────────────────────────────────────────────────────────
//
// The reporter is installed before `src/main.jsx` awaits the core, so that a
// core that fails to load is itself reported, and the rules that shape a
// report are in that core. So an event that arrives first is held, at most
// `HELD_BEFORE_CORE` of them, until the core answers: once it loads they go
// through the core like any other; if it fails they are sent as given, minus
// the stack, and so is anything after, up to the same bound. That path cleans,
// keys and counts nothing; the server cleans every payload against the same
// core before a line is logged (functions/clientEvents/report.js).

export const HELD_BEFORE_CORE = 5;
const PLATFORM = 'web';

let send = null;
let sent = 0;
const seen = [];
let core = 'waiting'; // 'ready' once it has answered, 'failed' if it never will
let watching = false;
let rawSent = 0;
const held = [];

/** `fn(data) => Promise`, the `reportClientEvent` callable. Null uninstalls. */
export function setClientEventSender(fn) {
  send = typeof fn === 'function' ? fn : null;
}

/** For tests: forget this page load's count, de-duplication and the core's answer. */
export function resetClientEvents() {
  sent = 0;
  seen.length = 0;
  core = 'waiting';
  watching = false;
  rawSent = 0;
  held.length = 0;
}

const asSent = (p) => Promise.resolve(p).then(() => true, () => false);

function deliver(name, payload) {
  if (!send) return Promise.resolve(false);
  const { stack, ...given } = payload || {};
  // The stack is hashed here rather than by whoever caught the error, so that
  // an error caught before the core loaded is hashed once it has.
  if (name === CLIENT_EVENTS.error && stack != null && given.stackHash == null) given.stackHash = stackHash(stack);
  const p = prepareClientEvent({ name, payload: { ...given, platform: PLATFORM }, appVersion: APP_VERSION, seen, sent });
  if (!p.send) return Promise.resolve(false);
  seen.push(p.key);
  sent += 1;
  return asSent(send({ name, payload: p.fields, appVersion: p.appVersion }));
}

function sendRaw(name, payload) {
  if (!send || rawSent >= HELD_BEFORE_CORE) return Promise.resolve(false);
  rawSent += 1;
  const given = { ...(payload || {}) };
  delete given.stack;
  return asSent(send({ name, payload: { ...given, platform: PLATFORM }, appVersion: APP_VERSION }));
}

const settleHeld = (how) => {
  for (const h of held.splice(0)) {
    try {
      h.resolve(how(h.name, h.payload));
    } catch {
      h.resolve(false);
    }
  }
};

function hold(name, payload) {
  if (core === 'failed') return sendRaw(name, payload);
  if (held.length >= HELD_BEFORE_CORE) return Promise.resolve(false);
  const answer = new Promise((resolve) => { held.push({ name, payload, resolve }); });
  if (!watching) {
    watching = true;
    loadCore().then(
      () => { core = 'ready'; settleHeld(deliver); },
      () => { core = 'failed'; settleHeld(sendRaw); },
    );
  }
  return answer;
}

function coreIsReady() {
  if (core === 'ready') return true;
  try {
    coreSync();
    core = 'ready';
    return true;
  } catch {
    return false;
  }
}

/**
 * Report one event. Resolves `true` if it was handed to the server, `false`
 * if it was dropped here or the call failed. Never rejects.
 */
export function reportClientEvent(name, payload = {}) {
  try {
    if (!send) return Promise.resolve(false);
    return coreIsReady() ? deliver(name, payload) : hold(name, payload);
  } catch {
    return Promise.resolve(false);
  }
}

/** How often SyncContext looks for a stalled queue, in milliseconds. */
export const stallCheckMs = () => clientEventPolicy().stallCheckMs;

/**
 * `{ depth, oldestMs }` for this studio's owed batches when the oldest has
 * been owed longer than the core's threshold, else null. `records` is the
 * queue's `records()`.
 */
export const queueStall = (records, { uid, now }) => clientSyncStall(records, { uid, now });

const routeOf = (win) => {
  try { return win.location?.pathname || null; } catch { return null; }
};

function errorPayload(error, fallbackMessage, source, win) {
  const message = error?.message || (typeof error === 'string' ? error : null) || fallbackMessage || 'Unknown error';
  // The stack as the browser printed it, hashed when it is delivered; with no
  // stack, the message stands in so one message is still one event.
  return {
    message,
    stack: error?.stack || message,
    route: routeOf(win),
    source,
  };
}

/** Report an error the app caught itself — a render boundary, say. */
export function reportClientError(error, source = 'render', win = globalThis.window) {
  return reportClientEvent(CLIENT_EVENTS.error, errorPayload(error, null, source, win));
}

/**
 * Uncaught errors and unhandled rejections, for the life of the page. Returns
 * the uninstaller.
 */
export function installClientErrorReporting(win = globalThis.window) {
  if (!win?.addEventListener) return () => {};
  const onError = (e) => {
    reportClientEvent(CLIENT_EVENTS.error, errorPayload(e?.error, e?.message, 'error', win));
  };
  const onRejection = (e) => {
    reportClientEvent(CLIENT_EVENTS.error, errorPayload(e?.reason, 'Unhandled promise rejection', 'unhandledrejection', win));
  };
  win.addEventListener('error', onError);
  win.addEventListener('unhandledrejection', onRejection);
  return () => {
    win.removeEventListener('error', onError);
    win.removeEventListener('unhandledrejection', onRejection);
  };
}
